import { createContext, useContext, useEffect } from 'react';
import { useComprehensionAssessment } from '@/hooks/use-comprehension-assessment';
import { ComprehensionAssessmentBanner } from './comprehension-assessment-banner';
import { useAuth } from '@/components/auth/AuthProvider';

// Create context to share assessment data across components
const ComprehensionAssessmentContext = createContext<ReturnType<typeof useComprehensionAssessment> | null>(null);

interface ComprehensionAssessmentProviderProps {
  children: React.ReactNode;
}

export function ComprehensionAssessmentProvider({ children }: ComprehensionAssessmentProviderProps) {
  const { user } = useAuth();
  const assessmentData = useComprehensionAssessment(); // Use hook only once here
  
  const {
    shouldShowAssessment,
    profile,
    isLoading,
    markAssessmentCompleted,
    getDaysUntilNextAssessment,
    isEligibleForReassessment
  } = assessmentData;

  // Don't show assessment for teachers (for now)
  const isStudent = user?.user_metadata?.user_type === 'student';
  
  const daysUntilNext = getDaysUntilNextAssessment();
  const isOverdue = daysUntilNext !== null && daysUntilNext < 0;
  const daysOverdue = isOverdue ? Math.abs(daysUntilNext || 0) : 0;

  return (
    <ComprehensionAssessmentContext.Provider value={assessmentData}>
      {isStudent && !isLoading && shouldShowAssessment && (
        <ComprehensionAssessmentBanner
          isReassessment={isEligibleForReassessment}
          daysOverdue={daysOverdue}
        />
      )}
      {children}
    </ComprehensionAssessmentContext.Provider>
  );
}

// Hook to access the shared assessment context
export function useComprehensionAssessmentContext() {
  const context = useContext(ComprehensionAssessmentContext);
  if (!context) {
    throw new Error('useComprehensionAssessmentContext must be used within ComprehensionAssessmentProvider');
  }
  return context;
}

// Hook to get current comprehension level for use in learning content
export function useStudentComprehensionLevel(): {
  level: number;
  score: number;
  hasAssessment: boolean;
  isLoading: boolean;
} {
  const { user } = useAuth();
  const { profile, isLoading, currentLevel, currentScore } = useComprehensionAssessmentContext(); // Use context instead
  
  const isStudent = user?.user_metadata?.user_type === 'student';
  
  return {
    level: isStudent ? currentLevel : 2, // Default to intermediate for non-students
    score: isStudent ? currentScore : 2.5,
    hasAssessment: !!profile,
    isLoading: isStudent ? isLoading : false
  };
}
